import { useState, useEffect, useCallback, useRef } from "react";
import { uGet, uPut, uDelete } from "../../utils/userApi"; // ← Changed to userApi
import { C, Input, Select, Btn, Table, Pagination, Modal } from "./AdminPanel";

// React Icons imports
import {
  FiSearch,
  FiEdit2,
  FiTrash2,
  FiUser,
  FiMail,
  FiPhone,
  FiCalendar,
  FiShield,
} from "react-icons/fi";
import { FaUserCircle, FaUserShield, FaUser } from "react-icons/fa";
import {
  MdAdminPanelSettings,
  MdPerson,
  MdEmail,
  MdPhone,
  MdDateRange,
} from "react-icons/md";

export default function Users({ toast }) {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editUser, setEditUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef();

  const load = useCallback(() => {
    setLoading(true);
    uGet("/users", { page, search }) // ← Now calls /api/users
      .then((d) => {
        setUsers(d.users);
        setTotal(d.total);
        setPages(d.pages);
      })
      .catch((e) => toast(e.message, "error"))
      .finally(() => setLoading(false));
  }, [page, search, toast]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(load, 400);
  }, [load]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Use uPut which goes to /api/users/:id
      await uPut(`/users/${editUser._id}`, {
        name: editUser.name,
        phone: editUser.phone,
        role: editUser.role,
      });
      toast("User updated ✓");
      setEditUser(null);
      load();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try {
      await uDelete(`/users/${id}`);
      toast("User deleted");
      load();
    } catch (e) {
      toast(e.message, "error");
    }
  };

  // Get role icon based on role
  const getRoleIcon = (role) => {
    if (role === "admin")
      return <MdAdminPanelSettings style={{ color: C.gold }} />;
    return <FaUserShield style={{ color: C.muted }} />;
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <Input
          placeholder="Search name or email…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          style={{ minWidth: 260 }}
          icon={<FiSearch />}
        />
        <span style={{ fontSize: 12, marginLeft: "auto" }}>
          <FiUser /> {total} users
        </span>
      </div>

      <div
        style={{
          border: `1px solid ${C.border}`,
          borderRadius: 14,
          overflow: "hidden",
        }}
      >
        <Table
          loading={loading}
          columns={[
            {
              key: "name",
              label: "Name",
              render: (v, row) => (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      background: C.primary + "33",
                      color: C.primary,
                      fontWeight: 800,
                      fontSize: 13,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {v?.charAt(0)?.toUpperCase()}
                  </div>
                  <span style={{ fontWeight: 600 }}>
                    <MdPerson /> {v}
                  </span>
                </div>
              ),
            },
            {
              key: "email",
              label: "Email",
              render: (v) => (
                <span style={{ color: C.muted, fontSize: 12 }}>
                  <FiMail /> {v}
                </span>
              ),
            },
            {
              key: "phone",
              label: "Phone",
              render: (v) => (
                <>
                  <FiPhone /> {v || "—"}
                </>
              ),
            },
            {
              key: "role",
              label: "Role",
              render: (v) => (
                <span
                  style={{
                    color: v === "admin" ? C.gold : C.muted,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    fontSize: 11,
                  }}
                >
                  {getRoleIcon(v)} {v}
                </span>
              ),
            },
            {
              key: "createdAt",
              label: "Joined",
              render: (v) => (
                <>
                  <FiCalendar /> {new Date(v).toLocaleDateString("en-IN")}
                </>
              ),
            },
            {
              key: "id", // ← Changed from _id to id (MySQL uses id)
              label: "Actions",
              render: (v, row) => (
                <div style={{ display: "flex", gap: 6 }}>
                  <Btn
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditUser({ ...row, _id: row.id })}
                  >
                    <FiEdit2 /> Edit
                  </Btn>
                  <Btn size="sm" variant="red" onClick={() => handleDelete(v)}>
                    <FiTrash2 />
                  </Btn>
                </div>
              ),
            },
          ]}
          data={users}
          rowKey="id" // ← Use id as key
        />
      </div>
      <Pagination page={page} pages={pages} onChange={setPage} />

      {editUser && (
        <Modal title="Edit User" onClose={() => setEditUser(null)}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <Input
              label="Full Name"
              value={editUser.name}
              onChange={(e) =>
                setEditUser((p) => ({ ...p, name: e.target.value }))
              }
              icon={<MdPerson />}
            />
            <Input
              label="Email"
              value={editUser.email}
              disabled
              style={{ opacity: 0.5 }}
              icon={<FiMail />}
            />
            <Input
              label="Phone"
              value={editUser.phone || ""}
              onChange={(e) =>
                setEditUser((p) => ({ ...p, phone: e.target.value }))
              }
              icon={<FiPhone />}
            />
            <Select
              label="Role"
              value={editUser.role}
              onChange={(e) =>
                setEditUser((p) => ({ ...p, role: e.target.value }))
              }
            >
              <option value="user">
                <FaUser /> User
              </option>
              <option value="admin">
                <MdAdminPanelSettings /> Admin
              </option>
            </Select>
            <div
              style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}
            >
              <Btn variant="ghost" onClick={() => setEditUser(null)}>
                Cancel
              </Btn>
              <Btn onClick={handleSave} disabled={saving}>
                {saving ? "Saving…" : "Save Changes"}
              </Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
